import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  RoomContentSourcesResponse,
  UpdateRoomContentSourcesPayload,
  getRoomContentSources,
  updateRoomContentSources,
} from '~/services/AccommodationService';

export const roomContentSourcesQueryKeys = {
  all: ['roomContentSources'] as const,
  detail: (roomId: string) => [...roomContentSourcesQueryKeys.all, 'detail', roomId] as const,
};

interface UseGetRoomContentSourcesParams {
  roomId: string;
  enabled?: boolean;
}

export function useGetRoomContentSources({ roomId, enabled = true }: UseGetRoomContentSourcesParams) {
  return useQuery({
    queryKey: roomContentSourcesQueryKeys.detail(roomId),
    queryFn: async ({ signal }) => {
      const response = await getRoomContentSources({ roomId }, { signal });
      return response.result;
    },
    enabled: enabled && !!roomId,
    refetchOnWindowFocus: false,
  });
}

interface UseUpdateRoomContentSourcesParams {
  roomId: string;
  data: UpdateRoomContentSourcesPayload;
}

export function useUpdateRoomContentSources() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roomId, data }: UseUpdateRoomContentSourcesParams) => {
      const response = await updateRoomContentSources({ roomId }, data);
      return response.result;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<RoomContentSourcesResponse>(roomContentSourcesQueryKeys.detail(variables.roomId), data);
    },
  });
}
